import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import SegmentedTabs from "./SegmentedTabs";

export const PortalExperienceContext = createContext(null);
export const PortalPageActionsContext = createContext(null);

export function useModernPortal() {
    return useContext(PortalExperienceContext)?.variant === "modern";
}

export function PortalModalScrollArea({ children }) {
    return useModernPortal()
        ? <div className="beta-modal-form__body">{children}</div>
        : <>{children}</>;
}

function actionText(node) {
    if (node === null || node === undefined || typeof node === "boolean") return "";
    if (typeof node === "string" || typeof node === "number") return String(node);
    if (Array.isArray(node)) return node.map(actionText).join(" ");
    if (React.isValidElement(node)) return actionText(node.props.children);
    return "";
}

function isCreateAction(node) {
    return /\b(create|add|request)\b/i.test(actionText(node));
}

function getBetaTablePages(page, pageCount) {
    if (pageCount <= 7) return Array.from({ length: pageCount }, (_, index) => index + 1);
    const pages = [1];
    if (page > 4) pages.push("start-gap");
    for (let value = Math.max(2, page - 1); value <= Math.min(pageCount - 1, page + 1); value += 1) {
        pages.push(value);
    }
    if (page < pageCount - 3) pages.push("end-gap");
    pages.push(pageCount);
    return pages;
}

function prioritizeActionList(children) {
    const items = React.Children.toArray(children);
    const prioritized = [...items].sort((left, right) => (
        Number(isCreateAction(right)) - Number(isCreateAction(left))
    ));
    return prioritized.map((item) => {
        if (!React.isValidElement(item)) return item;
        const nestedChildren = React.Children.toArray(item.props.children);
        if (nestedChildren.length < 2) return item;
        const prioritizedNestedChildren = prioritizeActionList(nestedChildren);
        const changed = prioritizedNestedChildren.some((child, index) => child !== nestedChildren[index]);
        return changed ? React.cloneElement(item, undefined, prioritizedNestedChildren) : item;
    });
}

export function PortalPageActions({ children }) {
    const experience = useContext(PortalExperienceContext);
    const prioritizedChildren = useMemo(() => prioritizeActionList(children), [children]);
    const prioritizedActions = prioritizedChildren.length === 1 ? prioritizedChildren[0] : prioritizedChildren;

    useEffect(() => {
        if (experience?.variant !== "modern" || !experience.setPageActions) {
            return undefined;
        }
        experience.setPageActions(prioritizedActions);
        return () => {
            experience.setPageActions((current) => current === prioritizedActions ? null : current);
        };
    }, [experience?.setPageActions, experience?.variant, prioritizedActions]);

    if (experience?.variant !== "modern") return children;
    return null;
}

export function PortalTabs({
    items = [],
    modernOnly = false,
    hideWhenSingle = false,
    ...props
}) {
    const isModernPortal = useModernPortal();

    if (modernOnly && !isModernPortal) return null;
    if (hideWhenSingle && items.length < 2) return null;

    const resolvedItems = items.map((item) => ({
        ...item,
        controls: item.controls || (
            modernOnly ? `portal-tab-panel-${item.id}` : undefined
        ),
        tabId: item.tabId || (
            modernOnly ? `portal-tab-${item.id}` : undefined
        ),
    }));

    return <SegmentedTabs {...props} items={resolvedItems} />;
}

export function PortalTabPanel({
    id,
    activeTab,
    children,
    className = "",
    modernOnly = true,
}) {
    const isModernPortal = useModernPortal();

    if (modernOnly && !isModernPortal) return <>{children}</>;
    if (activeTab !== id) return null;

    return (
        <div
            id={`portal-tab-panel-${id}`}
            role="tabpanel"
            aria-labelledby={`portal-tab-${id}`}
            className={`portal-tab-panel${className ? ` ${className}` : ""}`}
        >
            {children}
        </div>
    );
}

export function PortalSearchIcon() {
    return (
        <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <circle cx="10.8" cy="10.8" r="6.8" />
            <path d="m16 16 4.5 4.5" />
        </svg>
    );
}

function RefreshIcon() {
    return (
        <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <path d="M20 11a8 8 0 1 0-2.34 5.66" />
            <path d="M20 4v7h-7" />
        </svg>
    );
}

export function PortalFilterIcon() {
    return (
        <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <path d="M4 6h16M7 12h10M10 18h4" />
        </svg>
    );
}

export function PortalClearFiltersIcon() {
    return (
        <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <path d="M3 5h10M5.5 10h5M8 15h2" />
            <path d="m15 13 6 6m0-6-6 6" />
        </svg>
    );
}

function getFilterGroups(children) {
    const legacyBar = React.Children.toArray(children).find(
        (child) => child?.props?.className === "portal-filter-bar"
    );
    if (!legacyBar) return [];

    return React.Children.toArray(legacyBar.props.children)
        .filter((child) => {
            const className = child?.props?.className || "";
            return className.includes("portal-filter-bar__group") && !className.includes("--grow");
        })
        .map((group, index) => {
            const groupChildren = React.Children.toArray(group.props.children);
            const label = groupChildren.find((child) =>
                (child?.props?.className || "").includes("portal-filter-bar__label")
            );
            const controls = groupChildren.filter((child) => child !== label);
            return {
                id: `${label?.props?.children || "filter"}-${index}`.replace(/\W+/g, "-").toLowerCase(),
                label: label?.props?.children || "Filter",
                controls,
            };
        });
}

function hasActiveFilterControls(filterGroups) {
    return filterGroups.some((group) => group.controls.some((control) => {
        if (!React.isValidElement(control)) return false;
        if (control.type === "select") {
            const firstOption = React.Children.toArray(control.props.children)[0];
            const defaultValue = String(firstOption?.props?.value ?? "");
            return String(control.props.value ?? "") !== defaultValue;
        }
        if (control.type === "input") {
            if (control.props.type === "checkbox" || control.props.type === "radio") {
                return Boolean(control.props.checked);
            }
            return String(control.props.value ?? "").trim().length > 0;
        }
        return false;
    }));
}

export function PortalFilterGroupControls({ controls, groupLabel = "filter" }) {
    const [optionSearch, setOptionSearch] = useState("");
    const select = controls.find((control) => control?.type === "select");

    if (!select) {
        return <div className="beta-filter-menu__controls">{controls}</div>;
    }

    const options = React.Children.toArray(select.props.children);
    const selectedValue = String(select.props.value ?? "");
    const defaultValue = String(options[0]?.props?.value ?? "");
    const selectedValues = selectedValue
        ? selectedValue.split("|")
        : [defaultValue];
    const normalizedSearch = optionSearch.trim().toLowerCase();
    const optionEntries = options.map((option, optionIndex) => ({ option, optionIndex }));
    const visibleOptions = normalizedSearch
        ? optionEntries.filter(({ option }) => (
            actionText(option.props.children).toLowerCase().includes(normalizedSearch)
        ))
        : optionEntries;

    return (
        <>
            <label className="beta-filter-menu__search">
                <PortalSearchIcon />
                <input
                    type="search"
                    value={optionSearch}
                    onChange={(event) => setOptionSearch(event.target.value)}
                    placeholder="Search filters"
                    aria-label={`Search ${groupLabel} options`}
                    autoFocus
                />
            </label>
            <div
                className="beta-filter-menu__controls beta-filter-menu__options"
                role="listbox"
                aria-label={`${groupLabel} filter options`}
                aria-multiselectable="true"
            >
                {visibleOptions.map(({ option, optionIndex }) => {
                    const value = String(option.props.value ?? "");
                    const selected = selectedValues.includes(value);
                    const isDefault = optionIndex === 0;
                    return (
                        <button
                            key={value || "all"}
                            type="button"
                            className={`beta-filter-menu__option${selected ? " is-selected" : ""}${isDefault ? " is-default" : ""}`}
                            role="option"
                            aria-selected={selected}
                            onClick={() => {
                                if (isDefault) {
                                    select.props.onChange({
                                        target: { name: select.props.name, value },
                                    });
                                    return;
                                }

                                const nextValues = selectedValues.filter((selectedOption) => selectedOption !== defaultValue);
                                const nextValueSet = selected
                                    ? nextValues.filter((selectedOption) => selectedOption !== value)
                                    : [...nextValues, value];
                                const resolvedValues = nextValueSet.length ? nextValueSet : [defaultValue];
                                select.props.onChange({
                                    target: {
                                        name: select.props.name,
                                        value: resolvedValues.join("|"),
                                    },
                                });
                            }}
                        >
                            <span className="beta-filter-menu__checkbox" aria-hidden="true">
                                {selected ? "✓" : ""}
                            </span>
                            <span>{option.props.children}</span>
                        </button>
                    );
                })}
                {!visibleOptions.length && (
                    <div className="beta-filter-menu__empty">No matching options</div>
                )}
            </div>
        </>
    );
}

export function BetaDataTable({
    ariaLabel,
    columns = [],
    rows = [],
    loading = false,
    skeletonRowCount = 6,
    getRowKey = (row) => row.id,
    onRowClick,
    getRowClassName,
    getRowStyle,
}) {
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const pageCount = Math.max(1, Math.ceil(rows.length / pageSize));
    const rowSignature = rows.map((row) => String(getRowKey(row))).join("|");

    useEffect(() => {
        setPage(1);
    }, [rowSignature]);

    useEffect(() => {
        setPage((current) => Math.min(current, pageCount));
    }, [pageCount]);

    const firstResult = rows.length ? (page - 1) * pageSize + 1 : 0;
    const lastResult = Math.min(page * pageSize, rows.length);
    const visibleRows = rows.slice((page - 1) * pageSize, page * pageSize);
    const showSkeleton = loading && rows.length === 0;

    return (
        <section
            className={`beta-episode-table beta-data-table${showSkeleton ? " is-loading" : ""}`}
            aria-label={ariaLabel}
            aria-busy={loading}
        >
            <div className="beta-episode-table__scroll">
                <table>
                    <thead>
                        <tr>
                            {columns.map((column) => (
                                <th
                                    key={column.key}
                                    scope="col"
                                    className={column.className}
                                    style={column.width ? { width: column.width } : undefined}
                                >
                                    {column.label}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {showSkeleton && Array.from({ length: skeletonRowCount }, (_, rowIndex) => (
                            <tr className="beta-episode-table__row beta-data-table__skeleton-row" key={`skeleton-${rowIndex}`}>
                                {columns.map((column, columnIndex) => (
                                    <td
                                        key={column.key}
                                        className={columnIndex === 0 ? "beta-episode-table__identity" : undefined}
                                    >
                                        <span className="beta-data-table__skeleton-line" aria-hidden="true" />
                                        {columnIndex === 0 ? (
                                            <span className="beta-data-table__skeleton-line beta-data-table__skeleton-line--secondary" aria-hidden="true" />
                                        ) : null}
                                    </td>
                                ))}
                            </tr>
                        ))}
                        {visibleRows.map((row) => {
                            const clickable = typeof onRowClick === "function";
                            return (
                                <tr
                                    key={getRowKey(row)}
                                    className={[
                                        "beta-episode-table__row",
                                        getRowClassName ? getRowClassName(row) : "",
                                    ].filter(Boolean).join(" ")}
                                    style={getRowStyle ? getRowStyle(row) : undefined}
                                    onClick={clickable ? () => onRowClick(row) : undefined}
                                    onKeyDown={clickable ? (event) => {
                                        if (event.key === "Enter" || event.key === " ") {
                                            event.preventDefault();
                                            onRowClick(row);
                                        }
                                    } : undefined}
                                    tabIndex={clickable ? 0 : undefined}
                                >
                                    {columns.map((column, index) => (
                                        <td
                                            key={column.key}
                                            className={[index === 0 ? "beta-episode-table__identity" : "", column.className || ""].filter(Boolean).join(" ")}
                                        >
                                            {column.render ? column.render(row) : row[column.key]}
                                        </td>
                                    ))}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            <footer className="beta-episode-table__footer">
                <span>{showSkeleton ? "Loading…" : `${firstResult}–${lastResult} of ${rows.length} items`}</span>
                <label>
                    <span className="sr-only">Rows per page</span>
                    <select value={pageSize} disabled={loading} onChange={(event) => {
                        setPageSize(Number(event.target.value));
                        setPage(1);
                    }}>
                        {[10, 25, 50].map((size) => <option key={size} value={size}>{size}</option>)}
                    </select>
                </label>
                <nav aria-label={`${ariaLabel} pages`}>
                    <button type="button" onClick={() => setPage((current) => current - 1)} disabled={loading || page === 1}>‹</button>
                    {getBetaTablePages(page, pageCount).map((item) => typeof item === "string" ? (
                        <span key={item}>…</span>
                    ) : (
                        <button key={item} type="button" className={item === page ? "is-active" : ""} onClick={() => setPage(item)} disabled={loading}>{item}</button>
                    ))}
                    <button type="button" onClick={() => setPage((current) => current + 1)} disabled={loading || page === pageCount}>›</button>
                </nav>
            </footer>
        </section>
    );
}

export function PortalSearchFilterBar({
    search,
    onSearchChange,
    placeholder = "Search…",
    onRefresh,
    refreshing = false,
    filterContent,
    onClearFilters,
    actions,
    showFilters = true,
    children,
}) {
    const experience = useContext(PortalExperienceContext);
    const pageActions = useContext(PortalPageActionsContext);
    const isModernPortal = experience?.variant === "modern";
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [activeFilterId, setActiveFilterId] = useState(null);
    const rootRef = useRef(null);
    const filterGroups = getFilterGroups(children);
    const hasActiveFilters = hasActiveFilterControls(filterGroups);

    useEffect(() => {
        if (!isModernPortal || !experience.setToolbarMounted) return undefined;
        experience.setToolbarMounted(true);
        return () => experience.setToolbarMounted(false);
    }, [experience?.setToolbarMounted, isModernPortal]);

    const clearFilters = () => {
        if (onClearFilters) {
            onClearFilters();
            setActiveFilterId(null);
            return;
        }
        onSearchChange("");
        filterGroups.forEach((group) => {
            const select = group.controls.find((control) => control?.type === "select");
            const firstOption = React.Children.toArray(select?.props?.children)[0];
            if (select && firstOption) {
                select.props.onChange({
                    target: {
                        name: select.props.name,
                        value: String(firstOption.props.value ?? ""),
                    },
                });
            }
        });
        setActiveFilterId(null);
    };

    useEffect(() => {
        if (!filtersOpen) return undefined;
        const handlePointerDown = (event) => {
            const target = event.target;
            const clickedFilterArea = target instanceof Element && target.closest(
                ".beta-search-filter-bar__button, .beta-search-filter-bar__popover"
            );
            if (!clickedFilterArea) {
                setFiltersOpen(false);
                setActiveFilterId(null);
            }
        };
        const handleKeyDown = (event) => {
            if (event.key === "Escape") {
                setFiltersOpen(false);
                setActiveFilterId(null);
            }
        };
        document.addEventListener("pointerdown", handlePointerDown);
        document.addEventListener("keydown", handleKeyDown);
        return () => {
            document.removeEventListener("pointerdown", handlePointerDown);
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [filtersOpen]);

    useEffect(() => {
        if (!filtersOpen && activeFilterId !== null) {
            setActiveFilterId(null);
        }
    }, [activeFilterId, filtersOpen]);

    if (!isModernPortal) return children;

    return (
        <div className="beta-search-filter-bar" ref={rootRef}>
            <div className="beta-search-filter-bar__actions">
                {actions || pageActions}
            </div>
            <div className="beta-search-filter-bar__controls">
                {onRefresh ? (
                    <button
                        type="button"
                        className={`beta-search-filter-bar__button beta-search-filter-bar__refresh${refreshing ? " is-refreshing" : ""}`}
                        aria-label="Refresh list"
                        aria-busy={refreshing}
                        title="Refresh list"
                        onClick={onRefresh}
                        disabled={refreshing}
                    >
                        <RefreshIcon />
                    </button>
                ) : null}
                {showFilters ? (
                    <button
                        type="button"
                        className={`beta-search-filter-bar__button beta-search-filter-bar__filter${hasActiveFilters ? " has-active-filters" : ""}`}
                        aria-label="Show filters"
                        aria-expanded={filtersOpen}
                        data-active-filters={hasActiveFilters ? "true" : undefined}
                        onClick={() => {
                            setFiltersOpen((current) => !current);
                            setActiveFilterId(null);
                        }}
                    >
                        <PortalFilterIcon />
                    </button>
                ) : null}
                <label className="beta-search-filter-bar__search">
                    <PortalSearchIcon />
                    <input
                        type="search"
                        value={search}
                        onChange={(event) => onSearchChange(event.target.value)}
                        placeholder={placeholder}
                        aria-label={placeholder}
                    />
                </label>
                {showFilters && filtersOpen && (
                    <div className="beta-search-filter-bar__popover" role="dialog" aria-label="Search filters">
                    <div className="beta-search-filter-bar__popover-title">
                        <span>Search filters</span>
                        <button
                            type="button"
                            className="beta-search-filter-bar__clear"
                            aria-label="Clear all filters"
                            title="Clear all filters"
                            onClick={clearFilters}
                        >
                            <PortalClearFiltersIcon />
                        </button>
                    </div>
                    <div className="beta-search-filter-bar__fields">
                        {filterContent || filterGroups.map((group) => {
                            const isActive = activeFilterId === group.id;
                            return (
                                <div key={group.id} className="beta-filter-menu__group">
                                    <button
                                        type="button"
                                        className={`beta-filter-menu__trigger${isActive ? " is-active" : ""}`}
                                        aria-expanded={isActive}
                                        onClick={() => setActiveFilterId(isActive ? null : group.id)}
                                    >
                                        <span>{group.label}</span>
                                        <span aria-hidden="true">›</span>
                                    </button>
                                    {isActive && (
                                        <div className="beta-filter-menu__submenu">
                                            <PortalFilterGroupControls
                                                controls={group.controls}
                                                groupLabel={group.label}
                                            />
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export function PortalEmptyState({
    title,
    description,
    actionLabel = "",
    onAction,
    actionVariant = "secondary",
    compact = false,
}) {
    return (
        <section className={`portal-empty-state${compact ? " portal-empty-state--compact" : ""}`} aria-live="polite">
            <div className="portal-empty-state__icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" focusable="false">
                    <rect x="5" y="3.5" width="14" height="17" rx="2.5" />
                    <path d="M8.5 8h7M8.5 12h4.5" />
                    <circle cx="16.5" cy="16.5" r="3" />
                    <path d="m18.7 18.7 2 2" />
                </svg>
            </div>
            <div className="portal-empty-state__copy">
                <h3>{title}</h3>
                {description ? <p>{description}</p> : null}
            </div>
            {actionLabel && typeof onAction === "function" ? (
                <div className="portal-empty-state__actions">
                    <button
                        type="button"
                        className={`btn btn--${actionVariant}`}
                        onClick={onAction}
                    >
                        {actionLabel}
                    </button>
                </div>
            ) : null}
        </section>
    );
}

export function PortalSearchInput({ value, onChange, placeholder = "Search…", children }) {
    if (!useModernPortal()) return children;
    return (
        <label className="beta-search-filter-bar__search beta-search-filter-bar__search--standalone">
            <PortalSearchIcon />
            <input
                type="search"
                value={value}
                onChange={(event) => onChange(event.target.value)}
                placeholder={placeholder}
                aria-label={placeholder}
            />
        </label>
    );
}
